import { useEffect, useMemo, useRef, useState } from 'react';
import { MessageCircle, Sparkles, X } from 'lucide-react';
import { useAuth } from '../../state/AuthContext';
import { api, ApiError } from '../../api/client';
import type { KadieChatState, KadieMessage, LauncherPosition } from '../../types/kadie';
import { isApplicantRole } from '../../types/kadie';
import {
  clampLauncherPosition,
  clearChatState,
  getChatState,
  getLauncherPosition,
  getPreviewState,
  getSessionLoginTimestamp,
  setChatState,
  setLauncherPosition,
  setPreviewState,
} from '../../lib/kadieStorage';

const DRAG_THRESHOLD_PX = 5;
const MAX_MESSAGE_LENGTH = 4000;

export function KadieWidget() {
  const { user } = useAuth();
  // The universal switch on Portal → Kadie Audit. Hidden until the answer is known, so a turned-off
  // Kadie never flashes onto the page; a failed lookup keeps the site's old behavior (shown).
  const [enabled, setEnabled] = useState<boolean | null>(null);
  useEffect(() => {
    if (!user) return;
    let active = true;
    api.get<{ enabled: boolean }>('/api/kadie/status')
      .then((result) => { if (active) setEnabled(result.enabled); })
      .catch(() => { if (active) setEnabled(true); });
    return () => { active = false; };
  }, [user]);
  if (!user || !enabled) return null;
  return <KadieWidgetInner applicantId={user.id} firstName={user.name.split(' ')[0] || user.name} isAdmin={user.isAdmin} />;
}

function KadieWidgetInner({ applicantId, firstName, isAdmin }: { applicantId: string; firstName: string; isAdmin: boolean }) {
  const [position, setPosition] = useState<LauncherPosition>(() => getLauncherPosition(applicantId));
  const [open, setOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [messages, setMessages] = useState<KadieMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [thinking, setThinking] = useState(false);
  const [connected, setConnected] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewText, setPreviewText] = useState<string | null>(null);

  const dragState = useRef<{ startX: number; startY: number; startRight: number; startBottom: number; moved: boolean; pointerId: number } | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const initialized = useRef(false);

  // Restore conversation, messages, and draft on first mount.
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    const saved = getChatState(applicantId);
    if (saved) {
      setConversationId(saved.conversationId);
      setMessages(saved.messages);
      setDraft(saved.draft);
      setLoading(false);
      return;
    }
    void createConversation();
  }, [applicantId]);

  // Persist chat state whenever it changes.
  useEffect(() => {
    if (!conversationId) return;
    const state: KadieChatState = { conversationId, messages, draft };
    setChatState(applicantId, state);
  }, [applicantId, conversationId, messages, draft]);

  // Re-clamp launcher position on resize.
  useEffect(() => {
    function onResize() {
      setPosition((prev) => {
        const clamped = clampLauncherPosition(prev);
        setLauncherPosition(applicantId, clamped);
        return clamped;
      });
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [applicantId]);

  // Auto-scroll to bottom on new messages / thinking state.
  useEffect(() => {
    if (!open) return;
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [open, messages, thinking]);

  // Auto-focus input when panel opens.
  useEffect(() => {
    if (open) textareaRef.current?.focus();
  }, [open]);

  // One-time-per-session preview bubble, 50-70s after login, applicants only.
  useEffect(() => {
    if (isAdmin) return;
    const loginTimestamp = getSessionLoginTimestamp(applicantId);
    const state = getPreviewState(applicantId, loginTimestamp);
    if (state.shown || state.dismissed) return;
    const delay = 50000 + Math.random() * 20000;
    const timer = window.setTimeout(() => {
      if (open) return;
      setPreviewText(`Hi ${firstName}, I'm Kadie — need a hand with anything?`);
      setPreviewState(applicantId, loginTimestamp, { shown: true, dismissed: false });
    }, delay);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantId, isAdmin, firstName]);

  async function createConversation() {
    setLoading(true);
    setError(null);
    try {
      const conversation = await api.post<{ id: string; messages: KadieMessage[] }>('/api/kadie/conversations', { context: { route: window.location.pathname } });
      setConversationId(conversation.id);
      setMessages(conversation.messages);
      setConnected(true);
      return conversation.id;
    } catch (e) {
      setConnected(false);
      setError(e instanceof ApiError ? e.message : 'Could not connect to Kadie.');
      return null;
    } finally {
      setLoading(false);
    }
  }

  async function sendMessage() {
    const text = draft.trim();
    if (!text || thinking) return;
    let activeConversationId = conversationId;
    if (!activeConversationId) {
      activeConversationId = await createConversation();
      if (!activeConversationId) return;
    }

    const applicantMessage: KadieMessage = {
      id: `local-${Date.now()}`,
      role: 'APPLICANT',
      content: text,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, applicantMessage]);
    setDraft('');
    setThinking(true);
    setError(null);

    const context = { route: window.location.pathname };

    try {
      const result = await api.post<{ message: KadieMessage }>(`/api/kadie/conversations/${activeConversationId}/messages`, { content: text, context });
      setMessages((prev) => [...prev, result.message]);
      setConnected(true);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        clearChatState(applicantId);
        const newConversationId = await createConversation();
        if (newConversationId) {
          setMessages([applicantMessage]);
          try {
            const retry = await api.post<{ message: KadieMessage }>(`/api/kadie/conversations/${newConversationId}/messages`, { content: text, context });
            setMessages((prev) => [...prev, retry.message]);
            setConnected(true);
          } catch (retryError) {
            setConnected(false);
            setError(retryError instanceof ApiError ? retryError.message : 'Kadie is unavailable right now.');
          }
        }
      } else {
        setConnected(false);
        setError(e instanceof ApiError ? e.message : 'Kadie is unavailable right now.');
      }
    } finally {
      setThinking(false);
    }
  }

  function onLauncherPointerDown(e: React.PointerEvent<HTMLButtonElement>) {
    dragState.current = { startX: e.clientX, startY: e.clientY, startRight: position.right, startBottom: position.bottom, moved: false, pointerId: e.pointerId };
    e.currentTarget.setPointerCapture(e.pointerId);
    setIsDragging(true);
  }

  function onLauncherPointerMove(e: React.PointerEvent<HTMLButtonElement>) {
    const drag = dragState.current;
    if (!drag) return;
    const deltaX = e.clientX - drag.startX;
    const deltaY = e.clientY - drag.startY;
    if (!drag.moved && Math.hypot(deltaX, deltaY) < DRAG_THRESHOLD_PX) return;
    drag.moved = true;
    const next = clampLauncherPosition({ right: drag.startRight - deltaX, bottom: drag.startBottom - deltaY });
    setPosition(next);
  }

  function onLauncherPointerUp(e: React.PointerEvent<HTMLButtonElement>) {
    const drag = dragState.current;
    e.currentTarget.releasePointerCapture(e.pointerId);
    setIsDragging(false);
    if (!drag) return;
    if (drag.moved) {
      setLauncherPosition(applicantId, position);
    } else {
      setOpen((prevOpen) => !prevOpen);
      if (previewText) {
        setPreviewText(null);
        const loginTimestamp = getSessionLoginTimestamp(applicantId);
        setPreviewState(applicantId, loginTimestamp, { shown: true, dismissed: true });
      }
    }
    dragState.current = null;
  }

  function dismissPreview() {
    setPreviewText(null);
    const loginTimestamp = getSessionLoginTimestamp(applicantId);
    setPreviewState(applicantId, loginTimestamp, { shown: true, dismissed: true });
  }

  function openFromPreview() {
    setOpen(true);
    setPreviewText(null);
    const loginTimestamp = getSessionLoginTimestamp(applicantId);
    setPreviewState(applicantId, loginTimestamp, { shown: true, dismissed: true });
  }

  function onTextareaKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void sendMessage();
    }
  }

  const panelStyle = useMemo(
    () => ({ right: position.right, bottom: position.bottom + 56 + 12 }),
    [position],
  );

  return (
    <>
      {previewText && !open && (
        <div className="kadie-preview-bubble" style={{ right: position.right, bottom: position.bottom + 56 + 12 }}>
          <button type="button" className="kadie-preview-close" aria-label="Dismiss" onClick={dismissPreview}>
            <X size={14} />
          </button>
          <button type="button" className="kadie-preview-text" onClick={openFromPreview}>
            {previewText}
          </button>
        </div>
      )}

      {open && (
        <div className="kadie-panel" style={panelStyle}>
          <div className="kadie-panel-header">
            <Sparkles size={18} />
            <span className="kadie-panel-title">Kadie</span>
            <span className={`kadie-connection-status ${connected ? 'online' : 'offline'}`}>
              <span className="kadie-connection-dot" />
              {connected ? 'Connected' : 'Reconnecting…'}
            </span>
            <button type="button" className="kadie-panel-close" aria-label="Close Kadie" onClick={() => setOpen(false)}>
              <X size={18} />
            </button>
          </div>
          <div className="kadie-panel-body" ref={bodyRef}>
            {loading && <div className="kadie-status-line">Loading…</div>}
            {!loading && messages.length === 0 && !error && (
              <div className="kadie-empty-state">Ask Kadie anything about your application.</div>
            )}
            {messages.map((message) => (
              <div key={message.id} className={`kadie-message ${isApplicantRole(message.role) ? 'applicant' : 'assistant'}`}>
                <div className="kadie-message-bubble">{message.content}</div>
                {!isApplicantRole(message.role) && message.sourceNames && message.sourceNames.length > 0 && (
                  <div className="kadie-message-sources">Source: {message.sourceNames.join(', ')}</div>
                )}
              </div>
            ))}
            {thinking && (
              <div className="kadie-message assistant">
                <div className="kadie-message-bubble kadie-thinking">Kadie is thinking…</div>
              </div>
            )}
            {error && <div className="kadie-error-line">{error}</div>}
          </div>
          <div className="kadie-panel-footer">
            <textarea
              ref={textareaRef}
              className="kadie-input"
              rows={1}
              placeholder="Message Kadie…"
              value={draft}
              maxLength={MAX_MESSAGE_LENGTH}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onTextareaKeyDown}
            />
            <button type="button" className="kadie-send-button" onClick={() => void sendMessage()} disabled={!draft.trim() || thinking} aria-label="Send message">
              Send
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        className={`kadie-launcher ${isDragging ? 'dragging' : ''}`}
        style={{ right: position.right, bottom: position.bottom }}
        onPointerDown={onLauncherPointerDown}
        onPointerMove={onLauncherPointerMove}
        onPointerUp={onLauncherPointerUp}
        aria-label={open ? 'Close Kadie' : 'Open Kadie'}
      >
        {open ? <X size={26} /> : <MessageCircle size={26} />}
      </button>
    </>
  );
}
